import { defaultCharacterStyle, type CharacterStyle } from '../core/characters';
import { MAX_PLAYERS, PLAYER_COLORS, PLAYER_COLORS_CB } from '../core/players';
import type { MenuActions, MenuState } from './menus';
import type { UserSettings } from './settingsStore';
import { characterPreview } from './characterPreview';
import { MODE_HINTS, partySummary } from './partyOptions';
import { PARTY_PRESETS, renderPartyPresets, selectedPartyPreset } from './partyPresets';
import { partyBackdrop } from './partyBackdrop';
import './partyLobby.css';

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(label: string, action: () => void, primary = false): HTMLButtonElement {
  const node = element('button', `menu-btn${primary ? ' primary' : ''}`, label);
  node.type = 'button';
  node.addEventListener('click', action);
  return node;
}

function brand(): HTMLElement {
  const header = element('div', 'party-brand');
  const title = element('h1', 'party-wordmark', 'Floppy ');
  title.append(element('em', '', 'Clash'));
  header.append(title, element('span', 'party-brand-caption', 'A little floppy. A lot of fight.'));
  return header;
}

function navigation(actions: MenuActions): HTMLElement {
  const nav = element('nav', 'party-home-nav');
  nav.setAttribute('aria-label', 'More ways to play');
  for (const [label, action] of [['Local Play', 'local'], ['Quick Match', 'bots'], ['Practice', 'practice'], ['Online', 'online'], ['Level Editor', 'editor'], ['Settings', 'settings']]) {
    nav.append(button(label!, () => actions[action!]?.()));
  }
  return nav;
}

type StageFighter = {
  name: string;
  color: number;
  status: string;
  tag?: string;
  style: CharacterStyle;
  ready?: boolean;
  bot?: boolean;
  me?: boolean;
  slot: number;
  customize?: () => void;
};

function stage(fighters: StageFighter[], settings: UserSettings, invite: () => void, online: boolean): HTMLElement {
  const host = element('section', 'party-stage');
  host.setAttribute('aria-label', online ? 'Your online party' : 'Your fighters');
  const top = element('div', 'party-stage-top');
  top.append(element('h2', '', online ? 'Your party' : 'Your next brawl'), element('span', 'party-capacity', `${fighters.length} / ${MAX_PLAYERS} fighters`));
  const seats = element('div', 'seats party-stage-seats');
  const count = fighters.length > 4 ? MAX_PLAYERS : 4;
  seats.dataset.rows = count > 4 ? '2' : '1';
  seats.dataset.count = String(count);
  for (let i = 0; i < count; i++) {
    const fighter = fighters[i];
    const seat = element(fighter?.customize || !fighter ? 'button' : 'div', 'seat party-fighter');
    seat.dataset.seat = String(fighter?.slot ?? i);
    seat.dataset.taken = fighter && !fighter.bot ? '1' : '0';
    if (fighter?.bot) seat.dataset.bot = '1';
    if (fighter?.me) seat.dataset.me = '1';
    seat.dataset.ready = fighter?.ready ? '1' : '0';
    if (seat instanceof HTMLButtonElement) {
      seat.type = 'button';
      seat.addEventListener('click', fighter?.customize ?? invite);
      seat.setAttribute('aria-label', fighter ? 'Customize your fighter' : `Invite a friend to seat ${i + 1}`);
    }
    if (fighter) {
      const color = (settings.colorblind ? PLAYER_COLORS_CB : PLAYER_COLORS)[fighter.color] ?? PLAYER_COLORS[0]!;
      seat.style.setProperty('--c', color);
      const preview = characterPreview(fighter.style, color, { weapon: 'none', ready: fighter.ready, facing: i % 4 >= 2 ? -1 : 1, reducedMotion: settings.reduceShake });
      // Unarmed stage portraits can use a tighter camera than the weapon preview.
      preview.setAttribute('viewBox', '-0.8 -1.17 1.6 2.22');
      preview.setAttribute('preserveAspectRatio', 'xMidYMax meet');
      const pedestal = element('span', 'party-pedestal');
      pedestal.setAttribute('aria-hidden', 'true');
      seat.append(preview, pedestal, element('span', 'seat-name', fighter.name), element('span', 'seat-status', fighter.status), element('span', 'seat-pad', fighter.tag ?? ''));
      if (fighter.customize) seat.append(element('span', 'party-customize', 'Customize fighter'));
    } else {
      const plus = element('span', 'party-open-symbol', '+');
      plus.setAttribute('aria-hidden', 'true');
      seat.append(plus, element('span', 'seat-name', 'Open seat'), element('span', 'seat-status', 'Invite a friend'));
    }
    seats.append(seat);
  }
  host.append(top, seats);
  return host;
}

function selectedMode(state: MenuState, settings: UserSettings, online: boolean) {
  const config = online ? state.lobby.rules ?? { ...settings, ...state.lobby.partyRules, maxHp: state.lobby.maxHp, firstTo: state.lobby.firstTo } : { ...settings, ...state.party, maxHp: state.maxHp, firstTo: state.firstTo };
  const selected = selectedPartyPreset(config);
  const descriptor = PARTY_PRESETS.find((preset) => preset.id === selected);
  return { selected, title: descriptor?.title ?? 'Custom Brawl', description: descriptor?.description ?? partySummary(online ? state.lobby.partyRules : state.party) };
}

function modePanel(state: MenuState, settings: UserSettings, online: boolean): HTMLElement {
  const mode = selectedMode(state, settings, online);
  const panel = element('aside', 'party-play-panel');
  panel.append(element('span', 'party-eyebrow', 'Selected playlist'), element('h2', 'party-selected-title', mode.title), element('p', 'party-mode-description', mode.description));
  const rules = online ? state.lobby.partyRules : state.party;
  const length = rules.mode === 'arms-race' ? 'One race' : (online ? state.lobby.firstTo : state.firstTo) ? `First to ${online ? state.lobby.firstTo : state.firstTo}` : 'Endless rounds';
  panel.append(element('p', 'party-rule-line', `${online ? state.lobby.maxHp : state.maxHp} HP · ${length}`));
  if (online) {
    panel.append(element('p', 'lobby-mode-summary dim', partySummary(rules)));
  }
  return panel;
}

export function renderPartyHome(card: HTMLElement, state: MenuState, settings: UserSettings, actions: MenuActions, stats?: { matches: number; wins: number; kos: number }): void {
  card.classList.add('party-home');
  card.append(partyBackdrop(selectedMode(state, settings, false).selected));
  const header = element('header', 'party-header');
  header.append(brand(), navigation(actions));
  card.append(header);
  const humans = state.seats.map((seat, slot) => ({ ...seat, slot })).filter((seat) => seat.taken);
  const fighters: StageFighter[] = humans.length ? humans.map((seat, i) => ({
    name: i === 0 ? 'You' : `Player ${i + 1}`, color: seat.color, style: settings.characters[seat.color] ?? defaultCharacterStyle(seat.color),
    status: seat.ready ? 'READY' : 'LOCAL PLAYER', tag: seat.name || 'Local player', ready: seat.ready, me: i === 0, slot: seat.slot,
    customize: i === 0 ? () => actions.homeCustomize?.() : undefined,
  })) : [{ name: 'You', color: 0, style: settings.characters[0] ?? defaultCharacterStyle(0), status: 'READY TO PLAY', tag: 'Your fighter', me: true, slot: 0, customize: () => actions.homeCustomize?.() }];
  const used = new Set(fighters.map((fighter) => fighter.color));
  const bots = Math.min(state.bots, MAX_PLAYERS - fighters.length);
  for (let i = 0; i < bots; i++) {
    const color = PLAYER_COLORS.findIndex((_, color) => !used.has(color));
    used.add(color);
    fighters.push({ name: `Bot ${i + 1}`, color, style: settings.characters[color] ?? defaultCharacterStyle(color), status: 'CPU', tag: state.botDifficulty, bot: true, slot: fighters.length });
  }
  const main = element('div', 'party-main');
  main.append(stage(fighters, settings, () => actions.homeInvite?.(), false));
  const panel = modePanel(state, settings, false);
  const play = button('Play', () => actions.homePlay?.(), true);
  play.id = 'home-play';
  panel.append(play);
  const invite = element('div', 'party-invite-actions');
  invite.append(button('Invite friends', () => actions.homeInvite?.()), button('Join party', () => actions.online?.()));
  panel.append(invite);
  const options = element('div', 'party-home-options');
  const botsButton = button(`Bots: ${bots}`, () => actions.cycleBots?.());
  botsButton.id = 'cycle-bots';
  options.append(botsButton, button('Party rules', () => actions.partyRules?.()));
  panel.append(options, element('p', 'party-start-hint dim', humans.length > 1 ? 'Local players ready up together.' : 'Jump straight into a bot match.'));
  main.append(panel);
  card.append(main, renderPartyPresets(selectedMode(state, settings, false).selected, (id) => actions.preset?.(id)));
  const footer = element('footer', 'party-home-footer');
  const facts = element('div', 'party-home-facts');
  const stat = element('p', 'party-local-stats'); stat.id = 'localstats';
  if (stats) stat.textContent = `${stats.matches} matches · ${stats.wins} wins · ${stats.kos} KOs`;
  const hint = element('p', 'nav-hint'); hint.dataset.inputHint = 'navigation';
  facts.append(stat, hint); footer.append(facts); card.append(footer);
}

export function renderConnectedParty(card: HTMLElement, state: MenuState, settings: UserSettings, actions: MenuActions, syncFields: () => void): void {
  card.classList.add('party-home', 'party-connected');
  card.append(partyBackdrop(selectedMode(state, settings, true).selected));
  const lobby = state.lobby, isHost = lobby.role === 'host', me = lobby.peers.find((peer) => peer.me);
  const rematch = lobby.party?.phase === 'rematch';
  const active = lobby.inMatch && !rematch;
  const header = element('header', 'party-header');
  header.append(brand());
  const share = element('div', 'party-share');
  const room = element('span', 'party-room-label', 'Room '), code = element('strong', 'room-code', state.roomCode); code.id = 'room-code'; room.append(code);
  const link = element('code', 'party-share-url', lobby.shareUrl); link.id = 'share-url';
  share.append(room, link, button('Copy link', () => actions.copyLink?.()), button('Leave', () => actions.leave?.()));
  header.append(share); card.append(header);
  const bots = isHost ? Math.min(state.bots, MAX_PLAYERS - lobby.peers.length) : lobby.bots;
  const used = new Set(lobby.peers.map((peer) => peer.color));
  const fighters: StageFighter[] = [...lobby.peers].sort((a, b) => a.slot - b.slot).map((peer) => ({
    name: peer.name, color: peer.color, slot: peer.slot, style: peer.character ?? (peer.me ? settings.characters[peer.color] : undefined) ?? defaultCharacterStyle(peer.color),
    status: rematch ? 'MATCH FINISHED' : lobby.inMatch ? 'IN MATCH' : lobby.party?.ready.includes(peer.id) ? 'READY' : 'NOT READY',
    ready: lobby.party?.ready.includes(peer.id), me: peer.me,
    tag: [peer.host ? 'Host' : '', peer.me ? 'You' : '', ['teams', 'shared-stock'].includes(lobby.partyRules.mode) ? `${(lobby.partyRules.teams[peer.slot] ?? peer.slot % 2) === 0 ? 'Sun' : 'Moon'} team` : ''].filter(Boolean).join(' · '),
    customize: peer.me && !active ? () => actions.onlineCustomize?.() : undefined,
  }));
  for (let i = 0; i < bots; i++) {
    const color = PLAYER_COLORS.findIndex((_, color) => !used.has(color)); used.add(color);
    fighters.push({ name: `Bot ${i + 1}`, color, slot: fighters.length, style: defaultCharacterStyle(color), status: 'CPU', tag: lobby.rules?.botDifficulty ?? 'standard', bot: true });
  }
  const main = element('div', 'party-main');
  const left = element('div', 'party-left');
  left.append(stage(fighters, settings, () => actions.copyLink?.(), true));
  const chat = element('div', 'chat-log', state.chat.join('\n') || 'Your party chat. Say hi!');
  chat.setAttribute('role', 'log'); chat.setAttribute('aria-label', 'Party chat');
  const chatRow = element('div', 'chat-row');
  const input = element('input', ''); input.id = 'chat'; input.placeholder = 'Type a message'; input.maxLength = 200; input.value = state.draftChat;
  input.setAttribute('aria-label', 'Party message');
  input.addEventListener('input', () => { state.draftChat = input.value; });
  const send = () => { const text = input.value.trim(); if (!text) return; syncFields(); state.draftChat = ''; actions.chat?.(text); };
  input.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); send(); } });
  chatRow.append(input, button('Send', send));
  left.append(chat, chatRow); main.append(left);
  const panel = modePanel(state, settings, true);
  if (rematch) {
    panel.append(element('p', 'party-rematch-copy', 'Good fight. Run it back or pick the next brawl.'));
    const voted = !!me && !!lobby.party?.votes.includes(me.id);
    panel.append(button(voted ? 'Cancel vote' : 'Vote rematch', () => actions.voteOnline?.(), true));
    panel.append(element('p', 'dim lobby-rematch', `Rematch votes: ${lobby.party!.votes.length} / ${lobby.party!.members.length}`));
    panel.append(button(isHost ? 'Return to lobby' : 'Back to party', () => actions.returnLobby?.()));
  } else if (!lobby.inMatch) {
    const ready = !!me && !!lobby.party?.ready.includes(me.id);
    const readyButton = button(ready ? 'Not ready' : 'Ready', () => actions.readyOnline?.(), !isHost);
    readyButton.id = 'online-ready';
    readyButton.disabled = !lobby.party;
    if (isHost) {
      const start = button('Start', () => { syncFields(); actions.startOnline?.(); }, true);
      start.disabled = !lobby.party || !lobby.party.members.every((id) => lobby.party!.ready.includes(id));
      const readyActions = element('div', 'party-ready-actions');
      readyActions.append(readyButton, start);
      panel.append(readyActions, element('p', 'party-start-hint dim', start.disabled ? 'Ready up together, then start.' : 'Everyone is ready. Let’s brawl.'));
    } else {
      panel.append(readyButton, element('p', 'party-start-hint dim', 'The host chooses the playlist and starts the match.'));
    }
  } else {
    panel.append(element('p', 'lobby-status', isHost ? 'Your match is still running.' : 'A match is in progress. You’re in the next one.'));
    if (isHost) panel.append(button('Back to match', () => actions.startOnline?.(), true));
  }
  if (isHost && !lobby.inMatch) {
    const rules = element('div', 'party-lobby-rules');
    for (const [id, title, value, min] of [['hp', 'HP', lobby.maxHp, 1], ['ft', 'First to', lobby.firstTo, 0]] as const) {
      if (id === 'ft' && lobby.partyRules.mode === 'arms-race') continue;
      const label = element('label', '', title), field = element('input', '');
      field.id = id; field.type = 'number'; field.min = String(min); field.value = String(value);
      field.setAttribute('aria-label', title); field.addEventListener('change', () => { syncFields(); actions.lobbyChanged?.(); });
      label.append(field); rules.append(label);
    }
    panel.append(rules);
    const choices = element('div', 'party-home-options');
    const botsButton = button(`Bots: ${bots}`, () => actions.cycleBots?.()); botsButton.id = 'cycle-bots';
    choices.append(botsButton, button('Party rules', () => actions.partyRules?.())); panel.append(choices);
  }
  main.append(panel); card.append(main);
  const preset = selectedMode(state, settings, true).selected;
  card.append(renderPartyPresets(preset, (id) => actions.preset?.(id), { disabled: !isHost || lobby.inMatch }));
  const footer = element('footer', 'party-connected-footer');
  const hint = element('p', 'dim', 'Stay together between matches. The party closes if its host leaves.');
  const ping = element('p', 'dim party-ping', 'Relay '), value = element('span', '', lobby.ping ? String(lobby.ping) : '–'); value.id = 'ping';
  ping.append(value, ' ms'); footer.append(hint, button('Settings', () => actions.settings?.()), ping); card.append(footer);
  chat.scrollTop = chat.scrollHeight;
  if (lobby.peers.length + bots > 4) left.append(element('p', 'dim lobby-arena-hint', `${lobby.peers.length + bots} fighters · Large arenas with eight player starts`));
  // Keep the specific mode explanation discoverable without crowding the main play action.
  panel.title = MODE_HINTS[lobby.partyRules.mode];
}
